import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import useTrackEvent from "@/hooks/useTrackEvent";
import { LoadingSpinner, CustomizableSelect, IconClose } from "@velaro/velaro-component-lib";
import useAIConfigs from "@/hooks/useAIConfigs";
import useAzureIndexes from "@/hooks/useAzureIndexes";
import useSubscriptions from "@/hooks/useSubscriptions";
import useApi from "@/hooks/useApi";
import { AIConfiguration } from "../types";
import AINewSettingsTabs from "./AINewSettingsTabs";
import PromptEditor from "./PromptEditor";
import AIConfigVersionHistoryModal from "../AI/AIConfigVersionHistoryModal";

interface ModelOption {
  provider: string;
  label: string;
  techName: string;
}

const FALLBACK_MODELS: ModelOption[] = [
  { provider: "Anthropic", label: "Claude Haiku 4.5 — Standard", techName: "claude-haiku-4-5-20251001" },
  { provider: "Anthropic", label: "Claude Sonnet 4.6 — Advanced", techName: "claude-sonnet-4-6" },
  { provider: "OpenAI",    label: "GPT-4o Mini — Standard",       techName: "velaro-gpt-4o-mini" },
  { provider: "OpenAI",    label: "GPT-4o — Advanced",            techName: "velaro-gpt-4o" },
  { provider: "Google",    label: "Gemini 2.0 Flash — Standard",  techName: "gemini-2.0-flash" },
  { provider: "Google",    label: "Gemini 1.5 Pro — Advanced",    techName: "gemini-1.5-pro" },
];

interface SkillDescriptor {
  name: string;
  label: string;
  category: string;
}

export default function AINewSettingsGeneral() {
  const { id } = useParams();
  const { aiConfigurations, loading, update } = useAIConfigs();
  const { track } = useTrackEvent();
  const { azureIndexes } = useAzureIndexes();
  const { subscription } = useSubscriptions();
  const api = useApi();
  const [config, setConfig] = useState<AIConfiguration>();
  const [configIndex, setConfigIndex] = useState<number>();
  const [selectedIndexName, setSelectedIndexName] = useState<string>("");
  const [models, setModels] = useState<ModelOption[]>(FALLBACK_MODELS);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [availableSkills, setAvailableSkills] = useState<SkillDescriptor[]>([]);
  const [skillSearch, setSkillSearch] = useState("");

  useEffect(() => {
    api.messaging.get("AIConfiguration/available-models")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) setModels(data);
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  useEffect(() => {
    if (aiConfigurations.length > 0 && id) {
      const index = aiConfigurations.findIndex((c) => c.id === parseInt(id));
      if (index !== -1) {
        setConfig(aiConfigurations[index]);
        setConfigIndex(index);
        setSelectedIndexName(aiConfigurations[index].indexName);
      }
    }
  }, [aiConfigurations, id]);

  useEffect(() => {
    if (!id) return;
    let mounted = true;
    api.messaging.get(`AIConfiguration/${id}/available-skills`)
      .then((r) => r.json())
      .then((data) => { if (mounted && Array.isArray(data)) setAvailableSkills(data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, [id]);

  const handleUpdate = useCallback((field: keyof AIConfiguration, value: any) => {
    if (!config || configIndex === undefined) return;
    const updated = { ...config, [field]: value };
    setConfig(updated);
    update(updated, configIndex);
    track("setting_change", "AIBot/GeneralSettingsSaved");
  }, [config, configIndex, update, track]);

  const handleAzureIndex = (indexName: string) => {
    if (!config || configIndex === undefined) return;
    // When an index is selected: auto-enable vector search (was "None" = bot ignores index).
    // When the index is cleared: reset vectorSearchMethod back to "None".
    const updated = {
      ...config,
      indexName,
      vectorSearchMethod: indexName
        ? (config.vectorSearchMethod === "None" || !config.vectorSearchMethod ? "File" : config.vectorSearchMethod)
        : "None",
    };
    setConfig(updated);
    update(updated, configIndex);
    setSelectedIndexName(indexName);
  };

  const handleRestore = useCallback((partial: Partial<AIConfiguration>) => {
    if (!config || configIndex === undefined) return;
    const restored = { ...config, ...partial };
    setConfig(restored);
    update(restored, configIndex);
    setShowVersionHistory(false);
  }, [config, configIndex, update]);

  const languages = [
    { label: "Auto Detect" },
    { label: "English" },
    { label: "Spanish" }
  ];

  // Skill restriction derived state — computed once per render, not inline in JSX
  const isRestricting = !!config?.permittedSkillsJson && config.permittedSkillsJson !== "[]";

  const permittedSkills = useMemo<string[]>(() => {
    if (!config?.permittedSkillsJson) return [];
    try { return JSON.parse(config.permittedSkillsJson) as string[]; } catch { return []; }
  }, [config?.permittedSkillsJson]);

  const filteredSkills = useMemo(() => {
    if (!skillSearch.trim()) return availableSkills;
    const q = skillSearch.toLowerCase();
    return availableSkills.filter(
      (s) => s.label.toLowerCase().includes(q) || s.category.toLowerCase().includes(q)
    );
  }, [availableSkills, skillSearch]);

  const skillsByCategory = useMemo(() =>
    filteredSkills.reduce<Record<string, SkillDescriptor[]>>((acc, s) => {
      (acc[s.category] = acc[s.category] || []).push(s);
      return acc;
    }, {}),
    [filteredSkills]
  );

  const toggleSkill = useCallback((toolName: string, checked: boolean) => {
    const next = checked
      ? [...permittedSkills.filter((n) => n !== toolName), toolName]
      : permittedSkills.filter((n) => n !== toolName);
    handleUpdate("permittedSkillsJson", JSON.stringify(next));
  }, [permittedSkills, handleUpdate]);

  if (loading || !config) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <AINewSettingsTabs aiConfigId={id!} />

      {showVersionHistory && (
        <AIConfigVersionHistoryModal
          config={config}
          canExportImport={subscription?.enableAdvancedAIFeatures ?? false}
          close={() => setShowVersionHistory(false)}
          onRestore={handleRestore}
        />
      )}

      <div className="bg-white p-6 border rounded-2xl shadow-sm w-[750px] self-center mt-4">
        {/* Header */}
        <div className="border-b pb-4 mb-4">
          <div className="font-semibold text-lg">Chatbot Configuration</div>
          <div className="text-slate-500 text-sm">
            Configure your chatbot's behavior, instructions, and knowledge.
          </div>
        </div>

        {/* Internal Chatbot Name */}
        <div className="py-3">
          <div className="flex items-center justify-between pb-1">
            <div className="font-semibold text-sm">Internal Chatbot Name</div>
            <button
              type="button"
              onClick={() => handleUpdate("name", "")}
              className="text-xs text-slate-400 hover:text-slate-600"
              title="Clear name"
            >
              Clear
            </button>
          </div>
          <div className="text-slate-500 text-sm pb-2">
            A unique identifier for organizing and managing your chatbots within the app. Not visible to your customers or visitors.
          </div>
          <input
            type="text"
            value={config.name}
            onChange={(e) => handleUpdate("name", e.target.value)}
            placeholder="Enter a name"
            className="w-full p-2 border rounded-md text-sm"
          />
        </div>

        {/* Instructions — structured prompt editor */}
        <PromptEditor
          value={config.prompt}
          onChange={(prompt) => handleUpdate("prompt", prompt)}
          configName={config.name}
          onOpenVersionHistory={() => setShowVersionHistory(true)}
        />

        {/* AI Model */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">AI Model</div>
          <div className="text-slate-500 text-sm pb-2">
            The AI model will be used to generate answers for your chatbot.
          </div>
          {subscription?.enableCustomAiModel ? (
            <div className="text-sm">
              <CustomizableSelect
                options={models.map((m) => ({
                  label: m.provider,
                  sublabel: m.label,
                  action: () => handleUpdate("aiModel", m.techName)
                }))}
                defaultOption={(() => {
                  const model = models.find((m) => m.techName === config.aiModel) || models[0];
                  return {
                    label: model.provider,
                    sublabel: model.label,
                    action: () => handleUpdate("aiModel", model.techName)
                  };
                })()}
              />
            </div>
          ) : (
            <div className="text-sm text-slate-gray-500">
              {config.aiModel || "Platform Default (Claude Haiku 4.5)"}
              <span className="ml-2 text-xs text-slate-gray-400">— upgrade your plan or contact us to enable custom model selection</span>
            </div>
          )}
        </div>

        {/* Language (full width — reply length moved to Channel Overrides) */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Language</div>
          <div className="text-slate-500 text-sm pb-2">
            Default language for bot responses. Reply length is configured per channel under <strong>Channel Overrides</strong>.
          </div>
          <div className="w-1/2">
            <div className="text-sm">
              <CustomizableSelect
                options={languages.map((l) => ({
                  label: l.label,
                  action: () => handleUpdate("language", l.label)
                }))}
                defaultOption={(() => {
                  const lang = languages.find((l) => l.label === config.language) || languages[0];
                  return {
                    label: lang.label,
                    action: () => handleUpdate("language", lang.label)
                  };
                })()}
              />
            </div>
          </div>
        </div>

        {/* Response Creativity */}
        <div className="py-3">
          <div className="font-semibold text-sm pb-1">Response Creativity</div>
          <div className="text-slate-500 text-sm pb-2">
            Controls how consistent vs. creative your bot's responses are.
          </div>
          <div className="flex gap-2">
            {[
              { label: "Precise", value: 0.2, desc: "Consistent, predictable answers — best for FAQs and support" },
              { label: "Balanced", value: 0.5, desc: "Reliable but natural — recommended for most bots" },
              { label: "Creative", value: 0.9, desc: "More varied responses — best for conversational or sales bots" },
            ].map((opt) => {
              const current = config.temperature ?? 0.5;
              const isSelected = Math.abs(current - opt.value) < 0.05;
              return (
                <button
                  key={opt.label}
                  type="button"
                  title={opt.desc}
                  onClick={() => handleUpdate("temperature", opt.value)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors text-left ${
                    isSelected
                      ? "border-cornflower-blue-400 bg-cornflower-blue-50 text-cornflower-blue-700"
                      : "border-slate-200 bg-white text-slate-600 hover:border-cornflower-blue-200 hover:bg-slate-50"
                  }`}
                >
                  <div>{opt.label}</div>
                  <div className="text-xs font-normal text-slate-400 mt-0.5 leading-snug">{opt.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Link Delivery — IVR / Voice Calls */}
        {subscription?.enableBotSmsSkill && (
          <div className="py-3 border-t pt-4">
            <div className="font-semibold text-sm pb-1">Link Delivery (IVR / Voice)</div>
            <div className="text-slate-500 text-sm pb-3">
              When a caller asks for a link during a voice call, choose how the bot can send it.
            </div>
            <div className="space-y-2">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cornflower-blue-600 focus:ring-cornflower-blue-500"
                  checked={config.offerSmsLinks ?? true}
                  onChange={(e) => handleUpdate("offerSmsLinks", e.target.checked)}
                />
                <div>
                  <div className="text-sm font-medium text-slate-700">Offer to send via SMS</div>
                  <div className="text-xs text-slate-400">Bot offers to text the link to the caller's phone number</div>
                </div>
              </label>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-cornflower-blue-600 focus:ring-cornflower-blue-500"
                  checked={config.offerEmailLinks ?? false}
                  onChange={(e) => handleUpdate("offerEmailLinks", e.target.checked)}
                />
                <div>
                  <div className="text-sm font-medium text-slate-700">Offer to send via email</div>
                  <div className="text-xs text-slate-400">Bot offers to email the link to the caller's address on file</div>
                </div>
              </label>
            </div>
            {(config.offerSmsLinks ?? true) && (config.offerEmailLinks ?? false) && (
              <p className="text-xs text-cornflower-blue-600 mt-2">
                Both enabled — bot will ask the caller: "Would you prefer text or email?"
              </p>
            )}
          </div>
        )}

        {/* Appointment Booking Fallback */}
        {subscription?.enableIvr && (
          <div className="py-3 border-t pt-4">
            <div className="font-semibold text-sm pb-1">Appointment Booking</div>
            <div className="text-slate-500 text-sm pb-3">
              When no appointment booking integration (VAST, Square, Calendly) is active,
              tell the bot what to do when a caller or visitor asks to book.
            </div>
            <select
              className="w-full p-2 border rounded-md text-sm text-slate-700 mb-3"
              value={config.appointmentFallbackMode ?? 0}
              onChange={(e) => handleUpdate("appointmentFallbackMode", parseInt(e.target.value))}
            >
              <option value={0}>No instruction — bot uses its base prompt only</option>
              <option value={1}>Direct to website URL</option>
              <option value={2}>Transfer to live agent</option>
              <option value={3}>Collect info and arrange callback</option>
            </select>
            {(config.appointmentFallbackMode ?? 0) === 0 && (
              <p className="text-xs text-slate-400">
                No appointment-specific instruction is added to the prompt. The bot responds to booking requests using only what's in its base instructions above.
              </p>
            )}
            {(config.appointmentFallbackMode ?? 0) === 1 && (
              <div>
                <label className="text-xs font-medium text-slate-600 block mb-1">Booking website URL</label>
                <input
                  type="text"
                  className="w-full p-2 border rounded-md text-sm text-slate-700"
                  placeholder="https://yoursite.com/appointment"
                  value={config.appointmentFallbackUrl ?? ""}
                  onChange={(e) => handleUpdate("appointmentFallbackUrl", e.target.value)}
                />
                <p className="text-xs text-slate-400 mt-1">
                  On IVR: bot offers to text or email this link. In webchat: bot sends it directly.
                </p>
              </div>
            )}
            {(config.appointmentFallbackMode ?? 0) === 2 && (
              <p className="text-xs text-slate-400">
                On IVR: uses live transfer if configured, otherwise offers callback.
                In webchat: offers to connect with a live agent.
              </p>
            )}
            {(config.appointmentFallbackMode ?? 0) === 3 && (
              <div>
                <p className="text-xs text-slate-400 mb-2">
                  Bot collects the caller's name, phone number, and preferred time, then queues a callback request.
                  Works on IVR and webchat.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-md p-2">
                  <p className="text-xs text-blue-700 font-medium mb-0.5">Where callback data is stored</p>
                  <p className="text-xs text-blue-600">
                    Collected callbacks appear in <strong>Conversations</strong> — each callback creates a conversation record with the visitor's name, phone, and requested time in the conversation attributes. Your agents can view and action these from the messaging workspace.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Training Index */}
        <div className="py-3 border-t pt-4">
          <div className="font-semibold text-sm pb-1">Knowledge Index</div>
          <div className="text-slate-500 text-sm pb-2">
            Connect a knowledge index so this bot can answer questions from your training data. To create an index: go to <strong>Chatbot Training</strong>, run a scraper or upload files, then click <strong>Publish to Index</strong>. The new index will appear in this dropdown.
          </div>
          <div className="flex w-1/2 pr-3">
            <div className="h-[40px] text-sm flex flex-1">
              <CustomizableSelect
                key={selectedIndexName}
                options={azureIndexes.map((index) => ({
                  label: index.name,
                  action: () => handleAzureIndex(index.name)
                }))}
                defaultOption={{
                  label: selectedIndexName || "Select an index",
                  action: () => {}
                }}
              />
              {selectedIndexName && (
                <button className="pl-2" onClick={() => handleAzureIndex("")}>
                  <IconClose className="text-slate-gray-500" />
                </button>
              )}
            </div>
          </div>
          {selectedIndexName && config.vectorSearchMethod !== "None" && (
            <p className="text-xs text-green-600 mt-1">✓ Index connected — bot will search this knowledge base</p>
          )}
          {selectedIndexName && (config.vectorSearchMethod === "None" || !config.vectorSearchMethod) && (
            <p className="text-xs text-amber-600 mt-1">⚠ Index selected but vector search is off — save this page to activate</p>
          )}
          {!selectedIndexName && (
            <p className="text-xs text-slate-400 mt-1">No index connected — bot will answer from its instructions only</p>
          )}
        </div>

        {/* Skill Restrictions */}
        {availableSkills.length > 0 && (
          <div className="py-3 border-t pt-4">
            <div className="flex items-center justify-between mb-1">
              <div className="font-semibold text-sm">Skill Restrictions</div>
              <button
                type="button"
                onClick={() => handleUpdate("permittedSkillsJson", isRestricting ? null : JSON.stringify([]))}
                aria-label={isRestricting ? "Allow all skills" : "Restrict to specific skills"}
                className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                  isRestricting ? "bg-blue-600" : "bg-slate-300"
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    isRestricting ? "translate-x-4" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
            <div className="text-slate-500 text-sm pb-3">
              By default this bot can use all AI skills your subscription includes. Enable this to limit it to a specific set.
            </div>
            {isRestricting && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                  <input
                    type="text"
                    value={skillSearch}
                    onChange={(e) => setSkillSearch(e.target.value)}
                    placeholder="Search skills..."
                    className="flex-1 text-xs p-1.5 border border-slate-200 rounded-md"
                  />
                  <span className="text-xs text-slate-400 whitespace-nowrap">
                    {permittedSkills.length} / {availableSkills.length} selected
                  </span>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-slate-100">
                  {Object.entries(skillsByCategory).map(([category, skills]) => (
                    <div key={category}>
                      <div className="px-3 py-1.5 text-xs font-semibold text-slate-500 bg-slate-50 sticky top-0">
                        {category}
                      </div>
                      {skills.map((skill) => (
                        <label key={skill.name} className="flex items-center gap-2 px-3 py-1.5 hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={permittedSkills.includes(skill.name)}
                            onChange={(e) => toggleSkill(skill.name, e.target.checked)}
                            className="h-3.5 w-3.5 rounded border-slate-300"
                          />
                          <span className="text-xs text-slate-700">{skill.label}</span>
                          <span className="text-xs text-slate-400 font-mono ml-auto">{skill.name}</span>
                        </label>
                      ))}
                    </div>
                  ))}
                  {Object.keys(skillsByCategory).length === 0 && (
                    <div className="px-3 py-4 text-xs text-slate-400 text-center">No skills match your search</div>
                  )}
                </div>
                {permittedSkills.length === 0 && (
                  <div className="px-3 py-2 bg-amber-50 border-t border-amber-100">
                    <p className="text-xs text-amber-700">No skills selected — bot will have no tools available. Select at least one or disable this restriction.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Emergency & Safety Handling */}
        <div className="py-3 border-t pt-4">
          <div className="flex items-center justify-between mb-1">
            <div className="font-semibold text-sm">Emergency &amp; Safety Handling</div>
            <button
              type="button"
              onClick={() => handleUpdate("enableEmergencyHandling", !(config.enableEmergencyHandling ?? true))}
              aria-label={(config.enableEmergencyHandling ?? true) ? "Disable emergency handling" : "Enable emergency handling"}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
                (config.enableEmergencyHandling ?? true) ? "bg-blue-600" : "bg-slate-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  (config.enableEmergencyHandling ?? true) ? "translate-x-4" : "translate-x-0"
                }`}
              />
            </button>
          </div>
          <div className="text-slate-500 text-sm pb-3">
            When enabled, the bot detects emergency inputs (injury, threat, medical crisis) and
            responds with a safety advisory before continuing. Also limits responses to repeated
            inappropriate messages.
          </div>
          {(config.enableEmergencyHandling ?? true) && (
            <div className="space-y-3 pl-0">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Emergency advisory message
                </label>
                <input
                  type="text"
                  value={config.emergencyMessage ?? ""}
                  onChange={(e) => handleUpdate("emergencyMessage", e.target.value)}
                  placeholder="If this is an emergency, please hang up and dial 911."
                  className="w-full p-2 border rounded-md text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Shown when the bot detects a potential emergency. Leave blank for the default 911 advisory.
                </p>
              </div>
              <div className="w-48">
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Close session after <em>N</em> inappropriate messages
                </label>
                <select
                  className="w-full p-2 border rounded-md text-sm text-slate-700"
                  value={config.emergencyEscalationThreshold ?? 2}
                  onChange={(e) => handleUpdate("emergencyEscalationThreshold", parseInt(e.target.value))}
                >
                  <option value={1}>1 message</option>
                  <option value={2}>2 messages (recommended)</option>
                  <option value={3}>3 messages</option>
                  <option value={4}>4 messages</option>
                  <option value={5}>5 messages</option>
                </select>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
